/**
 * Le document d'une gamme d'entretien : ce qui s'imprime et ce qui s'exporte (registre B22, première
 * brique : « imprimer la gamme », demande de Benjamin du 2026-10-14).
 *
 * UNE seule fonction construit ce document, et l'impression comme l'export Excel en lisent le résultat —
 * jamais la gamme brute. C'est ce qui rend la règle « une variante sans prix n'a aucun prix » vraie
 * PARTOUT : un champ que la variante n'autorise pas n'est pas masqué, il n'existe tout simplement pas dans
 * le document (ni dans la page, ni dans le fichier).
 *
 * Trois variantes :
 * - SANS_PRIX : désignation, référence constructeur, quantité. Pour le client ;
 * - AVEC_PRIX : + prix public HT, total par ligne, sous-total par palier. Pour le client. LOT P2 (registre
 *   B26) : le « prix public » est le prix de VENTE calculé à la publication avec les conditions d'achat (jamais le
 *   prix du tarif du fabricant), et la variante n'existe que pour une version qui en porte ;
 * - INTERNE : + référence et prix des équivalents (l'« adaptable ») avec leurs sous-totaux, les remarques de
 *   chaque ligne (provenance, rappels : ce sont des notes internes) et, quand la version porte des prix calculés,
 *   le prix du tarif, le prix de vente, le coût d'achat réel et la MARGE de chaque palier. Une version sans prix
 *   calculés garde la lecture d'avant (prix du tarif, coût d'achat estimé). Jamais pour le client : le document
 *   le dit sur chaque page.
 *
 * Fonctions pures, sans navigateur : tout se teste sur des nombres.
 */
import type { GammeCritere, GammeVersion, StatutVersionGamme } from '../api/client';
import { GENRE_LIGNE, dateFr, decrireNiveau, niveauxAvecPrix } from './gammes';
import { libelleCritere } from './gammes-identite';
import {
  aDesPrixDAchat,
  aDesPrixDeVente,
  niveauxAvecPrixCalcules,
  prixDeLaLigne,
  type GammeLigneVue,
  type GammeNiveauVue,
} from './gammes-prix-calcules';
import {
  type MargePalier,
  type SousTotalAdaptable,
  type SousTotalPalier,
  margePalier,
  sousTotalAdaptable,
  sousTotalPalier,
  sousTotauxDeLaVersion,
  totalAdaptableLigneCentimes,
  totalLigneCentimes,
} from './gammes-sous-totaux';

export type VarianteGamme = 'SANS_PRIX' | 'AVEC_PRIX' | 'INTERNE';

export const VARIANTES_GAMME: readonly VarianteGamme[] = ['SANS_PRIX', 'AVEC_PRIX', 'INTERNE'];

export const TEXTE_VARIANTE: Record<VarianteGamme, { libelle: string; description: string; exigePrix: boolean }> = {
  SANS_PRIX: {
    libelle: 'Sans prix',
    description: 'Désignations, références et quantités. À remettre au client.',
    exigePrix: false,
  },
  AVEC_PRIX: {
    libelle: 'Avec prix',
    description: 'Prix public HT (prix de vente), total par ligne et sous-total par palier. À remettre au client.',
    exigePrix: true,
  },
  INTERNE: {
    libelle: 'Interne',
    description:
      'Tout, y compris le prix du tarif, le coût d’achat réel et la marge, les équivalents (références, prix, sous-totaux) et les remarques internes. Ne jamais remettre au client.',
    exigePrix: true,
  },
};

/**
 * La variante « avec prix » est réservée aux versions qui portent des prix de VENTE (registre B26, lot P2) : elle
 * montrait, depuis le lot P1 jusqu'ici, le prix du TARIF du fabricant sous le titre « Prix public HT » — or le prix
 * de vente de Benjamin n'est pas le prix du tarif, il se reconstitue par remise puis coefficient. Une version
 * publiée AVANT le calcul des prix de vente (ou dont les conditions d'achat n'étaient pas saisies) n'a donc pas de
 * variante « avec prix » : un prix faux remis à un client serait pire que pas de prix.
 */
export const MOTIF_AVEC_PRIX_INDISPONIBLE =
  'Cette version ne porte pas de prix de vente : saisis tes conditions d’achat puis recalcule les gammes de la marque (écran « Conditions d’achat »). Le prix du tarif n’est pas ton prix de vente.';

/**
 * Pourquoi une variante n'est pas disponible pour cette version, ou `null` si elle l'est. `aPrixDeVente` : la version
 * porte au moins un prix de vente calculé.
 */
export function varianteDisponible(variante: VarianteGamme, aPrixDeVente: boolean): string | null {
  return variante === 'AVEC_PRIX' && !aPrixDeVente ? MOTIF_AVEC_PRIX_INDISPONIBLE : null;
}

/** Ce que dit, en rouge, tout document interne. */
export const AVERTISSEMENT_INTERNE = 'DOCUMENT INTERNE — ne pas remettre au client';

/** La machine pour laquelle on imprime, quand on part de sa fiche (facultatif). */
export interface MachineDocument {
  readonly designation: string;
  readonly modele: string | null;
  readonly referenceConstructeur: string | null;
  readonly numeroSerie: string | null;
  readonly site: string | null;
  readonly client: string | null;
}

/**
 * Une ligne du document. Les clés de prix, d'équivalent et de remarque sont ABSENTES (pas nulles) quand
 * la variante ne les autorise pas : c'est ce que vérifient les tests des variantes pour le client.
 */
export interface LigneDocument {
  readonly designation: string;
  /** « Contrôle », « Si nécessaire » ; `null` pour un remplacement (le cas général, rien à dire). */
  readonly genre: string | null;
  readonly reference: string | null;
  readonly quantite: number | null;
  readonly unite: string | null;
  /** Remarque de la ligne : INTERNE seulement (provenance, rappels — rien à montrer à un client). */
  readonly note?: string | null;
  /**
   * Le prix de la source du document (`DocumentGamme.source`) : le prix de VENTE (ce que voit le client) ou, pour une
   * version sans prix calculés lue en interne, le prix du TARIF du fabricant.
   */
  readonly prixPublic?: number | null;
  /** Total de la ligne à ce prix, en centimes entiers ; `null` quand elle ne compte pas (voir les sous-totaux). */
  readonly totalCentimes?: number | null;
  /** INTERNE avec prix de vente seulement : le prix du TARIF du fabricant, en information. */
  readonly prixTarif?: number | null;
  /** INTERNE avec coût d'achat calculé seulement : le coût d'achat réel (celui de l'équivalent s'il y en a un). */
  readonly prixAchat?: number | null;
  readonly totalAchatCentimes?: number | null;
  readonly referenceAdaptable?: string | null;
  readonly prixAdaptable?: number | null;
  /** Total de la ligne au prix de l'équivalent, en centimes entiers ; `null` sans équivalent chiffré. */
  readonly totalAdaptableCentimes?: number | null;
}

export interface PalierDocument {
  readonly code: string;
  readonly libelle: string;
  /** « tous les 6 000 h, le premier à 3 000 h ». */
  readonly cadence: string;
  readonly lignes: readonly LigneDocument[];
  /** Seulement avec les prix. */
  readonly sousTotal?: SousTotalPalier;
  /** INTERNE seulement, et seulement si une ligne du palier a un équivalent chiffré. */
  readonly sousTotalAdaptable?: SousTotalAdaptable;
  /** INTERNE avec coût d'achat calculé : la somme des coûts d'achat réels du palier. */
  readonly sousTotalAchat?: SousTotalPalier;
  /** INTERNE avec prix de vente ET coût d'achat calculés : la marge du palier. */
  readonly marge?: MargePalier;
}

export interface DocumentGamme {
  readonly variante: VarianteGamme;
  readonly avecPrix: boolean;
  readonly interne: boolean;
  /**
   * D'où vient le prix des colonnes « prix » et des sous-totaux : le prix de VENTE (la variante « avec prix », et la
   * variante interne d'une version qui en porte) ou le prix du TARIF (variante interne d'une version sans prix
   * calculés) ; `null` sans prix.
   */
  readonly source: 'VENTE' | 'TARIF' | null;
  /** INTERNE : la version porte des coûts d'achat réels (les colonnes « coût d'achat » et la marge existent). */
  readonly avecAchat: boolean;
  /**
   * INTERNE : au moins une ligne des paliers imprimés a un équivalent (référence ou prix). Sans équivalent nulle part,
   * les trois colonnes « équivalent » n'auraient que des tirets : elles ne s'impriment pas et la désignation s'élargit.
   */
  readonly avecEquivalents: boolean;
  /** Raison sociale de l'espace, quand le compte peut la lire. */
  readonly entreprise: string | null;
  readonly code: string;
  readonly titre: string;
  readonly marque: string;
  readonly modeles: readonly string[];
  readonly references: readonly string[];
  readonly version: {
    readonly numero: number;
    readonly statut: StatutVersionGamme;
    readonly enVigueur: boolean;
    readonly effet: string;
  };
  readonly machine: MachineDocument | null;
  readonly paliers: readonly PalierDocument[];
  /** Un sous-total par palier choisi, seulement avec les prix. */
  readonly recapitulatif?: readonly SousTotalPalier[];
  /** INTERNE sans coût d'achat calculé : le côté équivalents de chaque palier choisi qui en a (coût ESTIMÉ). */
  readonly recapitulatifAdaptable?: readonly SousTotalAdaptable[];
  /** INTERNE avec coût d'achat calculé : la somme des coûts d'achat réels de chaque palier choisi. */
  readonly recapitulatifCout?: readonly SousTotalPalier[];
  /** INTERNE avec prix de vente et coût d'achat calculés : la marge de chaque palier choisi qui en a une. */
  readonly recapitulatifMarge?: readonly MargePalier[];
  /** Jour d'édition, « 2026-10-14 ». */
  readonly edition: string;
}

export interface EntreeDocument {
  readonly gamme: {
    readonly code: string;
    readonly label: string;
    readonly brandName: string;
    readonly criteres: readonly GammeCritere[];
    readonly referencesConstructeur: readonly string[];
  };
  readonly version: GammeVersion;
  /** Vrai si c'est la version qui s'applique aujourd'hui aux machines. */
  readonly enVigueur: boolean;
  readonly variante: VarianteGamme;
  /** `levelKey` des paliers à imprimer ; `null` = tous. */
  readonly paliersChoisis: ReadonlySet<string> | null;
  readonly peutVoirPrix: boolean;
  readonly machine?: MachineDocument | null;
  readonly entreprise?: string | null;
  readonly edition: string;
}

export type ResultatDocument =
  | { readonly ok: true; readonly document: DocumentGamme }
  | { readonly ok: false; readonly raison: string };

function ligneDocument(
  ligne: GammeLigneVue,
  variante: VarianteGamme,
  source: 'VENTE' | 'TARIF' | null,
  avecAchat: boolean,
): LigneDocument {
  const avecPrix = variante !== 'SANS_PRIX' && source !== null;
  const interne = variante === 'INTERNE';
  return {
    designation: ligne.designation,
    genre: ligne.kind === 'REPLACE' ? null : GENRE_LIGNE[ligne.kind].texte,
    reference: ligne.reference,
    quantite: ligne.quantity,
    unite: ligne.unit,
    ...(interne ? { note: ligne.note } : {}),
    ...(avecPrix ? { prixPublic: prixDeLaLigne(ligne, source), totalCentimes: totalLigneCentimes(ligne, source) } : {}),
    // Le prix du TARIF n'est montré en plus qu'en interne, quand le prix principal est celui de vente.
    ...(interne && source === 'VENTE' ? { prixTarif: ligne.publicPrice ?? null } : {}),
    ...(interne && avecAchat
      ? { prixAchat: prixDeLaLigne(ligne, 'ACHAT'), totalAchatCentimes: totalLigneCentimes(ligne, 'ACHAT') }
      : {}),
    ...(interne
      ? {
          referenceAdaptable: ligne.adaptableReference,
          prixAdaptable: ligne.internalPrice ?? null,
          totalAdaptableCentimes: totalAdaptableLigneCentimes(ligne),
        }
      : {}),
  };
}

function palierDocument(
  niveau: GammeNiveauVue,
  variante: VarianteGamme,
  source: 'VENTE' | 'TARIF' | null,
  avecAchat: boolean,
): PalierDocument {
  const sousTotal = variante === 'SANS_PRIX' || source === null ? null : sousTotalPalier(niveau, source);
  const interne = variante === 'INTERNE';
  const adaptable = interne ? sousTotalAdaptable(niveau) : null;
  const achat = interne && avecAchat ? sousTotalPalier(niveau, 'ACHAT') : null;
  const marge = interne && avecAchat && source === 'VENTE' ? margePalier(niveau) : null;
  return {
    code: niveau.code,
    libelle: niveau.label,
    cadence: decrireNiveau(niveau),
    lignes: niveau.lines.map((l) => ligneDocument(l, variante, source, avecAchat)),
    ...(sousTotal ? { sousTotal } : {}),
    ...(adaptable ? { sousTotalAdaptable: adaptable } : {}),
    ...(achat ? { sousTotalAchat: achat } : {}),
    ...(marge ? { marge } : {}),
  };
}

export interface RemarquesPalier {
  /** Par ligne du palier : le numéro de sa remarque (à partir de 1), ou `null` quand elle n'en a pas. */
  readonly renvois: readonly (number | null)[];
  /** Les remarques du palier, numérotées dans l'ordre où elles apparaissent ; un texte identique n'y figure qu'une fois. */
  readonly remarques: readonly string[];
}

/**
 * Les remarques de ligne (provenance, rappels : INTERNE seulement) s'impriment EN BAS du palier, sur toute la largeur,
 * avec un numéro de renvoi dans la désignation. Écrites dans la colonne de désignation, elles s'enroulaient sur douze
 * lignes et faisaient déborder le palier sur une page presque vide.
 */
export function remarquesDuPalier(lignes: readonly LigneDocument[]): RemarquesPalier {
  const remarques: string[] = [];
  const renvois = lignes.map((ligne) => {
    const texte = (ligne.note ?? '').trim();
    if (texte === '') return null;
    let rang = remarques.indexOf(texte);
    if (rang < 0) {
      remarques.push(texte);
      rang = remarques.length - 1;
    }
    return rang + 1;
  });
  return { renvois, remarques };
}

/**
 * Construit le document d'une variante, ou dit pourquoi on ne le peut pas. Refuse une variante qui
 * montre des prix à qui n'a pas le droit de les voir, et une version dont l'API n'a rendu aucun prix :
 * on ne fabrique jamais un document « avec prix » vide de prix.
 */
export function construireDocumentGamme(entree: EntreeDocument): ResultatDocument {
  const { variante, version } = entree;
  const avecPrix = variante !== 'SANS_PRIX';
  const interne = variante === 'INTERNE';

  if (avecPrix && !entree.peutVoirPrix) {
    return { ok: false, raison: 'Cette variante montre des prix : votre compte n’a pas le droit de les voir.' };
  }
  // Les niveaux de la version, chaque ligne enrichie de ses prix calculés (jamais renvoyés au serveur).
  const toutesLesLignes = niveauxAvecPrixCalcules(version);
  if (avecPrix && !niveauxAvecPrix(toutesLesLignes)) {
    return { ok: false, raison: 'Les prix de cette version ne sont pas disponibles : le document ne peut pas les montrer.' };
  }
  // La version porte-t-elle un prix de vente / un coût d'achat ? Jugé sur TOUTE la version, pas sur les paliers choisis :
  // la disponibilité d'une variante ne dépend pas de ce qu'on coche.
  const aVente = aDesPrixDeVente(toutesLesLignes);
  const avecAchat = interne && aDesPrixDAchat(toutesLesLignes);
  if (variante === 'AVEC_PRIX' && varianteDisponible(variante, aVente) !== null) {
    return { ok: false, raison: MOTIF_AVEC_PRIX_INDISPONIBLE };
  }
  // Le prix des colonnes « prix » : vente pour le client ; en interne, la vente si elle existe, sinon le tarif.
  const source: 'VENTE' | 'TARIF' | null = !avecPrix ? null : variante === 'AVEC_PRIX' || aVente ? 'VENTE' : 'TARIF';

  const niveaux = toutesLesLignes.filter(
    (n) => entree.paliersChoisis === null || entree.paliersChoisis.has(n.levelKey),
  );
  if (niveaux.length === 0) {
    return { ok: false, raison: 'Choisissez au moins un palier à imprimer.' };
  }

  const recapitulatif = source === null ? null : sousTotauxDeLaVersion(niveaux, source);
  // Sans coût d'achat réel, la variante interne garde la lecture d'avant : un coût d'achat ESTIMÉ.
  const adaptables =
    interne && !avecAchat ? niveaux.map(sousTotalAdaptable).filter((s): s is SousTotalAdaptable => s !== null) : [];
  const couts = avecAchat ? sousTotauxDeLaVersion(niveaux, 'ACHAT') : null;
  const marges = avecAchat && source === 'VENTE' ? niveaux.map(margePalier).filter((m): m is MargePalier => m !== null) : [];
  const modeles = [...new Set(entree.gamme.criteres.map(libelleCritere))];
  const avecEquivalents =
    interne && niveaux.some((n) => n.lines.some((l) => l.adaptableReference !== null || (l.internalPrice ?? null) !== null));

  return {
    ok: true,
    document: {
      variante,
      avecPrix,
      interne,
      source,
      avecAchat,
      avecEquivalents,
      entreprise: entree.entreprise ?? null,
      code: entree.gamme.code,
      titre: entree.gamme.label,
      marque: entree.gamme.brandName,
      modeles,
      references: entree.gamme.referencesConstructeur,
      version: {
        numero: version.versionNumber,
        statut: version.statut,
        enVigueur: entree.enVigueur,
        effet: version.validFrom,
      },
      machine: entree.machine ?? null,
      paliers: niveaux.map((n) => palierDocument(n, variante, source, avecAchat)),
      ...(recapitulatif ? { recapitulatif } : {}),
      ...(adaptables.length > 0 ? { recapitulatifAdaptable: adaptables } : {}),
      ...(couts ? { recapitulatifCout: couts } : {}),
      ...(marges.length > 0 ? { recapitulatifMarge: marges } : {}),
      edition: entree.edition,
    },
  };
}

/** « Version 6, en vigueur depuis le 14/10/2026 » ; « Brouillon — non publié » ; « Version 4, archivée ». */
export function phraseVersion(version: DocumentGamme['version']): string {
  if (version.statut === 'BROUILLON') return `Version ${version.numero} — brouillon, non publié`;
  if (version.statut === 'ARCHIVEE') return `Version ${version.numero}, archivée`;
  return version.enVigueur
    ? `Version ${version.numero}, en vigueur depuis le ${dateFr(version.effet)}`
    : `Version ${version.numero}, d’effet au ${dateFr(version.effet)}`;
}

/** « Édité le 14/10/2026 » : le jour où le document a été produit. */
export function phraseEdition(document: DocumentGamme): string {
  return `Édité le ${dateFr(document.edition)}`;
}

/**
 * Le nom du document : « KAESER ESD442SFC – ESD.3 (sans prix) ». C'est le titre de la page, donc le nom que
 * Chrome et Edge proposent en « Enregistrer au format PDF », et la base du nom du classeur Excel. La
 * variante y figure : on ne confond pas un document interne avec celui du client.
 */
export function nomDocument(document: DocumentGamme): string {
  const base = document.titre.replace(/\s*·\s*réf\.\s*/i, ' – ').trim();
  return `${base} (${TEXTE_VARIANTE[document.variante].libelle.toLowerCase()})`;
}

/** Le nom de fichier : celui du document, sans les caractères que Windows refuse dans un nom. */
export function nomFichier(document: DocumentGamme, extension: string): string {
  return `${nomDocument(document).replace(/[\\/:*?"<>|]+/g, '-')}.${extension}`;
}

export type CleColonne =
  | 'designation'
  | 'reference'
  | 'quantite'
  | 'tarif'
  | 'prix'
  | 'total'
  | 'refAdaptable'
  | 'prixAdaptable'
  | 'totalAdaptable'
  | 'prixAchat'
  | 'totalAchat';

export interface ColonneFeuille {
  readonly cle: CleColonne;
  readonly titre: string;
  readonly classe: string;
}

/**
 * Les colonnes de la feuille, selon la variante et la source du prix. Une seule liste pour l'en-tête, les lignes et les
 * sous-totaux : un colSpan écrit à la main se décale dès qu'une colonne apparaît.
 */
export function colonnesFeuille(doc: DocumentGamme): ColonneFeuille[] {
  const vente = doc.source === 'VENTE';
  const colonnes: ColonneFeuille[] = [
    { cle: 'designation', titre: 'Désignation', classe: '' },
    { cle: 'reference', titre: 'Référence', classe: 'col-reference' },
    { cle: 'quantite', titre: 'Qté', classe: 'col-qte' },
  ];
  // Le prix du TARIF n'apparaît en plus qu'en interne, quand le prix principal est celui de vente.
  if (doc.avecPrix && doc.interne && vente) colonnes.push({ cle: 'tarif', titre: 'Prix du tarif HT', classe: 'col-nombre' });
  if (doc.avecPrix) {
    colonnes.push(
      { cle: 'prix', titre: vente ? (doc.interne ? 'Prix de vente HT' : 'Prix public HT') : 'Prix du tarif HT', classe: 'col-nombre' },
      { cle: 'total', titre: vente ? (doc.interne ? 'Total vente HT' : 'Total HT') : 'Total au tarif HT', classe: 'col-nombre' },
    );
  }
  if (doc.interne) {
    // Les trois colonnes « équivalent » ne s'impriment que si une ligne a un équivalent : sinon elles n'ont que des
    // tirets et écrasent la désignation (onze colonnes sur une feuille A4).
    if (doc.avecEquivalents) {
      colonnes.push(
        { cle: 'refAdaptable', titre: 'Réf. adaptable', classe: 'col-reference' },
        { cle: 'prixAdaptable', titre: 'Prix adaptable HT', classe: 'col-nombre' },
        { cle: 'totalAdaptable', titre: 'Total adaptable HT', classe: 'col-nombre' },
      );
    }
    if (doc.avecAchat) {
      colonnes.push(
        { cle: 'prixAchat', titre: 'Prix d’achat HT', classe: 'col-nombre' },
        { cle: 'totalAchat', titre: 'Total achat HT', classe: 'col-nombre' },
      );
    }
  }
  return colonnes;
}
